import React, { ReactElement, useCallback, useEffect } from 'react';
import { Button, Form, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Dispatch } from 'redux';
import IDeal from '@/Api/Deal/IDeal';
import IDealProperty, {
    IDealPropertyErrors,
} from '@/Api/Deal/IDealProperty';
import {
    dealCompleteAction,
    dealGetAction,
    propertyAddAction,
    propertyErrorSetAction,
} from '@/Lead/actions';
import {
    dealSelector,
    propertiesSelector,
    savingSelector,
} from '@/Lead/selectors';
import { validateProperty } from '@/Lead/validators';
import Property from './Property';
import PlusSign from '@/UI/PlusSign';
import LeftDrawerLayout from './LeftDrawerLayout';

export default function Properties(): ReactElement {
    const dispatch: Dispatch = useDispatch();
    const deal: IDeal = useSelector(dealSelector);
    const properties: IDealProperty[] = useSelector(propertiesSelector);
    const saving: boolean = useSelector(savingSelector);

    useEffect(() => {
        if (!deal.uuid) {
            dispatch(dealGetAction());
        }
    }, [
        deal,
        dispatch,
    ]);

    const propertyAdd: () => void = useCallback((): void => {
        dispatch(propertyAddAction());
    }, [
        dispatch,
    ]);

    const validateProperties: () => boolean = useCallback((): boolean => {
        let valid: boolean = true;

        properties.forEach((property: IDealProperty) => {
            const setError: (key: keyof IDealPropertyErrors, value: string) => void = (key: keyof IDealPropertyErrors, value: string): void => {
                dispatch(propertyErrorSetAction(property.uuid, key, value));
            };

            valid = validateProperty(property, setError) && valid;
        });

        return valid;
    }, [
        dispatch,
        properties,
    ]);

    const onClickContinue: () => void = useCallback((): void => {
        let valid: boolean = true;

        valid = validateProperties() && valid;

        if (!valid) {
            return;
        }

        dispatch(dealCompleteAction(true, '/get-your-quote/thanks', '/get-your-quote/quote'));
    }, [
        dispatch,
        validateProperties,
    ]);

    if (!deal.uuid) {
        return (
            <LeftDrawerLayout currentStep={4} pageName='properties'>
                <h3 className='headline'>Securing your loan</h3>
                <Spin indicator={<LoadingOutlined spin={true} />} />
            </LeftDrawerLayout>
        );
    }

    const propertiesBlock: ReactElement[] = properties.map((property: IDealProperty, index: number) => (
        <Property index={index} key={property.uuid} uuid={property.uuid} />
    ));

    return (
        <LeftDrawerLayout currentStep={4} pageName='properties'>
            <Helmet>
                <title>Funding.com.au</title>
                <meta content={'Enter in your property details and you\'ll be on your way to moving forward faster.'} name='description' />
            </Helmet>
            <h3 className='headline'>Securing your loan</h3>
            <p className='intro'>
                To secure your loan, we&apos;ll need details about the property.
            </p>

            <Form>
                <p className='header'>Tell us about your secured property</p>
                {propertiesBlock}
                <Button
                    className='add-property'
                    disabled={saving}
                    icon={<PlusSign />}
                    loading={saving}
                    variant='outlined'
                    onClick={propertyAdd}
                >
                    Add another property
                </Button>
            </Form>
            <div className='actions'>
                <Link to='/get-your-quote/loan-purpose'><Button className='previous' type='primary'>Back</Button></Link>
                <Button type='primary' onClick={onClickContinue}>Save and continue</Button>
            </div>
        </LeftDrawerLayout>
    );
}

